import type { CSSProperties, FunctionComponent } from "react";
import { createPortal } from "react-dom";
import { Toaster as Sonner, type ToasterProps as SonnerProps } from "sonner";

export type ToasterProps = Omit<SonnerProps, "style" | "toastOptions">;

// sonner ships its own greys; bind its CSS variables to the palette so toasts follow the theme.
// Set inline on the toaster element, so they beat sonner's `[data-sonner-theme]` defaults.
const PALETTE_VARS = {
  "--normal-bg": "var(--bg-elevated)",
  "--normal-bg-hover": "var(--bg-hover)",
  "--normal-border": "var(--border)",
  "--normal-border-hover": "var(--border-strong)",
  "--normal-text": "var(--text)",
} as CSSProperties;

// sonner's stylesheet is unlayered, so it beats Tailwind's utility layer — hence the `!` overrides.
const TOAST_CLASS_NAMES = {
  toast: "shadow-lg! shadow-noa-shadow!",
  description: "text-noa-muted!",
  success: "[&_[data-icon]]:text-noa-success!",
  info: "[&_[data-icon]]:text-noa-info!",
  warning: "[&_[data-icon]]:text-noa-warning!",
  error: "[&_[data-icon]]:text-noa-danger!",
};

/**
 * The one `<Toaster />` that `message` / `notification` (toast.ts) render into, themed from the palette.
 * Portaled to `body`, like Dialog: `#root` is an isolated stacking context, so a toaster inside it would
 * always sit under a dialog's viewport.
 */
export const Toaster: FunctionComponent<ToasterProps> = (props) =>
  createPortal(
    <Sonner style={PALETTE_VARS} toastOptions={{ classNames: TOAST_CLASS_NAMES }} {...props} />,
    document.body
  );
